/**
 * Circle Marker Utility Functions
 * Handles circle marker calculations and configurations
 */

import {
  GisCircleMarker,
} from '@app/pages/electronic-field-map/pages/gis-fields/interfaces/gis-component.interfaces';
import {map} from 'rxjs';
import {OpenStreetMapComponent} from '@components/openstreetmap/openstreetmap.component';
import {PolygonConverterUtil} from '@app/utils/polygon-converter.util';
import {GisPolygon} from '@components/openstreetmap/interfaces/gis-component.interfaces';

/**
 * Calculate circle radius based on field count using logarithmic scale
 * NOTE: Now returns a fixed pixel radius instead of dynamic meter-based radius
 * This ensures circles don't grow when zooming in
 * @param count Number of fields in the region (parameter kept for backwards compatibility)
 * @param baseRadius Base radius in pixels (default: 30px)
 * @returns Fixed radius in pixels
 */
export function calculateCircleRadius(count: number, baseRadius: number = 30): number {
  // Return fixed pixel radius - circles will not grow with zoom
  return baseRadius;
}

/**
 * Create a circle marker for a region with field count
 * @param regionId Unique identifier for the region
 * @param count Number of fields in the region
 * @param center Center coordinates [lat, lng]
 * @param polygon Associated region polygon
 * @param color Associated color
 * @param mapInstance instance of the map (optional, can be used for future enhancements)
 * @returns GisCircleMarker object
 */
export function createRegionCircleMarker(
  regionId: string,
  count: number,
  center: [number, number],
  polygon: GisPolygon,
  color: string = '#4CAF50',
  mapInstance?: OpenStreetMapComponent
): GisCircleMarker {
  const radius = calculateCircleRadius(count);
const  maxZoom = polygon.data?.level === 2 ? 9 : 11
  return {
    id: `region-count-${regionId}`,
    center: center,
    radius: radius,
    count: count,
    fillColor: color,
    strokeColor: color,
    fillOpacity: 0.6,
    strokeWidth: 2,
    type: 'region-count',
    data: {
      regionId: regionId,
      regionName: polygon.name,
      fieldCount: count,
      level: polygon.data?.level
    },
    // Use same zoom levels as the region polygon
    showByZoomLevel: polygon.showByZoomLevel,
    minZoom: polygon.minZoom,
    maxZoom: maxZoom,
    onClick: (circle: GisCircleMarker) => {
      const layerController  = mapInstance?.getLayersController()

      if (!layerController) {return}

      const center  =  PolygonConverterUtil.getPolygonCenter(polygon.coordinates)

      if (polygon.data?.level  ===  2) {
        layerController.centerToPoint(center?.lat as number, center?.lng as number, 10)
      }else  {
        layerController.centerToPoint(center?.lat as number, center?.lng as number, 12)
      }

    },
    onHover: (circle: GisCircleMarker) => {
    }
  };
}

/**
 * Create multiple circle markers for regions with field counts
 * @param regionsCount Map of region IDs to field counts
 * @param regionPolygonsCache Cache of region polygons with their centers
 * @param color color for the circle markers
 * @param mapInstance instance of the map (optional, can be used for future enhancements)
 * @returns Array of GisCircleMarker objects
 */
export function createRegionCircleMarkers(
  regionsCount: { [key: string]: number; },
  regionPolygonsCache: Map<string, { polygon: GisPolygon; center: [number, number] }>,
  color = '#4CAF50',
  mapInstance: any
): GisCircleMarker[] {
  const circles: GisCircleMarker[] = [];
  const regionsCountMap = new Map<string, number>(Object.entries(regionsCount));
  regionsCountMap.forEach((count: number, regionId: string) => {
    const cachedData = regionPolygonsCache.get(regionId);

    if (cachedData && count > 0) {
      const {polygon, center} = cachedData;
      const circle = createRegionCircleMarker(
        regionId,
        count,
        center,
        polygon,
        color,
        mapInstance
      );


      circles.push(circle);
    }
  });

  return circles;
}
